<script setup lang="ts">
import { getSubscriptionLink } from '@plf/utils/company'
import { ESubscriptionType } from '@types'
import { Mascot } from '@ui/components/icons/mascot'
import { useNow } from '@vueuse/core'

const props = withDefaults(
  defineProps<{
    label?: string
    mini?: boolean
    variant?: 'default' | 'expired'
    showExpiration?: boolean
  }>(),
  {
    label: '',
    mini: true,
    variant: 'default',
    showExpiration: true,
  },
)

const accountStore = useAccountStore()
const goToProLink = computed(() => getSubscriptionLink(accountStore.activeCompanyId))
const now = useNow()

const tariff = computed(() => accountStore.activeCompany?._tariff?.type)
const expiration = computed(() => accountStore.activeCompany?._tariff?._expiration)

const isVisible = computed(() => {
  if (props.variant === 'expired') {
    return true
  }

  if (tariff.value !== ESubscriptionType.TARIFF_PRO) {
    return true
  }

  if (!expiration.value) {
    return false
  }

  const expirationDate = new Date(expiration.value)

  if (Number.isNaN(expirationDate.getTime())) {
    return false
  }

  const diffMs = expirationDate.getTime() - now.value.getTime()
  const threeDaysMs = 3 * 24 * 60 * 60 * 1000

  return diffMs >= 0 && diffMs <= threeDaysMs
})

function formatExpiration() {
  return formatDate(expiration.value, 'dd MMMM yyyy')
}

const mascot = computed(() => {
  if (props.variant === 'expired') {
    return Mascot.Wonder
  }

  return tariff.value === ESubscriptionType.TARIFF_PRO ? Mascot.Sunglass : Mascot.Wink
})

function getDefaultLabel() {
  switch (tariff.value) {
    case ESubscriptionType.TARIFF_PRO:
      return 'layout.cto.pro'
    case ESubscriptionType.TARIFF_START:
      return 'layout.cto.starter'
    default:
      return 'layout.cto.go_pro'
  }
}

function getDescription() {
  if (props.showExpiration && expiration.value) {
    return 'layout.cto.tariff_description'
  }
  if (props.variant === 'expired') {
    return 'layout.cto.subscription_expired_description'
  }

  return 'layout.cto.go_pro_description'
}
</script>

<template lang="pug">
template(v-if="isVisible")
  NuxtLink.flex-center-center.col-0.w-100p.max-w-296.m-auto.text-none(:to="goToProLink")
    UiIcon.s-60.z-index-1(:name="mascot")
    .plaque.flex-center-center.br-20.p-32-20-20.min-h-104(v-if="!mini")
      .flex-start-center.col-4.w-100p
        .label.typ-16-24-600.text-center {{ $t(variant === 'expired' ? 'layout.cto.subscription_expired' : label || getDefaultLabel()) }}
        .description.typ-12-18-400.text-center {{ $t(getDescription(), { expiration: formatExpiration() }) }}
        slot(:to="goToProLink")
</template>

<style scoped>
.plaque {
  background: var(--cue-bg-surface-highest);
  width: calc(100% - 16px);
  margin-top: -28px;
}

.label {
  align-content: center;
  color: var(--cue-content-neutral-primary);
}

.description {
  color: var(--cue-content-neutral-secondary);
}
</style>
